import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { CalendarDays, Wand2, Edit3, Trash2, Timer, CircleDollarSign, Check, Loader2, CalendarOff, Plus, CalendarPlus, FileText, FileSpreadsheet, FileDown, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { jadwalTamanAPI, settingsAPI } from "../lib/api";
import { exportToExcel, exportToPDF, exportToWord } from "../lib/exportUtils";
import LaporanKegiatanDialog from "../components/LaporanKegiatanDialog";
import { useApp } from "../context/AppContext";

const formatIDR = (n) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(n || 0);
const BULAN_ID = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const HARI_ID = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
const formatFullDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return iso;
  return `${HARI_ID[d.getDay()]}, ${d.getDate()} ${BULAN_ID[d.getMonth()]} ${d.getFullYear()}`;
};

const JadwalTaman = ({ petugas, canEdit, onReloadPetugas }) => {
  const { theme, updateTheme } = useApp();
  const [jadwal, setJadwal] = useState([]);
  const [rekap, setRekap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [genForm, setGenForm] = useState({ tanggal_mulai: new Date().toISOString().slice(0, 10), jam_default: "07:00" });
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [upahLocal, setUpahLocal] = useState(theme?.upahHarianTukangTaman || 60000);
  const [savingUpah, setSavingUpah] = useState(false);
  const [lapPrefill, setLapPrefill] = useState(null);

  useEffect(() => {
    setUpahLocal(theme?.upahHarianTukangTaman || 60000);
  }, [theme?.upahHarianTukangTaman]);

  const load = () => {
    if (!petugas?.id) return;
    setLoading(true);
    Promise.all([
      jadwalTamanAPI.list(petugas.id).catch(() => []),
      jadwalTamanAPI.rekap(petugas.id).catch(() => null),
    ]).then(([j, r]) => { setJadwal(j || []); setRekap(r); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [petugas?.id]);

  const generate = async () => {
    if (!genForm.tanggal_mulai) { toast.error("Pilih tanggal mulai"); return; }
    try {
      const r = await jadwalTamanAPI.generate({ petugas_id: petugas.id, tanggal_mulai: genForm.tanggal_mulai, jam_default: genForm.jam_default, replace_existing: true });
      toast.success(`${r.count} hari jadwal ter-generate`);
      setGenOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal generate"); }
  };

  const lanjutkan = async () => {
    const lastHari = jadwal[jadwal.length - 1]?.hari_ke;
    if (!window.confirm(`Generate siklus berikutnya (bulan depan), melanjutkan dari H-${lastHari}? Tanggal mulai otomatis sehari setelah jadwal terakhir dan semua hari dihitung NORMAL (bukan lembur).`)) return;
    try {
      const r = await jadwalTamanAPI.lanjutkan({ petugas_id: petugas.id, jam_default: "07:00" });
      toast.success(`${r.count} hari siklus baru dibuat (mulai H-${r.mulai_hari_ke})`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal lanjutkan jadwal"); }
  };

  const saveUpah = async () => {
    const val = parseInt(upahLocal) || 60000;
    setSavingUpah(true);
    try {
      await settingsAPI.update({ upahHarianTukangTaman: val });
      await updateTheme({ upahHarianTukangTaman: val });
      toast.success("Upah harian diperbarui");
      load();
    } catch { toast.error("Gagal simpan upah"); }
    finally { setSavingUpah(false); }
  };

  const openEdit = (row) => { setEditForm({ ...row }); setEditOpen(true); };
  const saveEdit = async () => {
    try {
      await jadwalTamanAPI.update(editForm.id, {
        tanggal: editForm.tanggal, jam: editForm.jam, area: editForm.area,
        tugas: editForm.tugas, estimasi_hari: parseInt(editForm.estimasi_hari) || 1,
        status: editForm.status, is_lembur: !!editForm.is_lembur, catatan: editForm.catatan,
      });
      toast.success("Jadwal diperbarui");
      setEditOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal simpan"); }
  };

  const toggleStatus = async (row) => {
    const next = row.status === "selesai" ? "belum" : row.status === "libur" ? "belum" : "selesai";
    try { await jadwalTamanAPI.update(row.id, { status: next }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const markLibur = async (row) => {
    if (!window.confirm(`Tandai H-${row.hari_ke} (${formatFullDate(row.tanggal)}) sebagai LIBUR / Tidak Masuk?\nSemua jadwal setelahnya otomatis bergeser +1 hari.`)) return;
    try {
      await jadwalTamanAPI.libur(row.id);
      toast.success("Ditandai LIBUR — jadwal berikutnya bergeser +1 hari");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const tambahHari = async (row) => {
    if (!window.confirm(`Tambah 1 hari kerja ekstra untuk area "${row.area}" setelah H-${row.hari_ke}?\nSemua jadwal setelahnya otomatis bergeser +1 hari.`)) return;
    try {
      await jadwalTamanAPI.tambahHari({ after_id: row.id });
      toast.success("1 hari kerja ditambahkan — jadwal berikutnya bergeser +1 hari");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const removeAll = async () => {
    if (!window.confirm(`Hapus SEMUA (${jadwal.length}) baris jadwal ${petugas.nama}?`)) return;
    try { await jadwalTamanAPI.clear(petugas.id); toast.success("Jadwal dihapus"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const statusLabel = (s) => s === "selesai" ? "Selesai" : s === "berjalan" ? "Berjalan" : s === "libur" ? "Libur/Tidak Masuk" : "Belum";

  const doExport = (kind) => {
    if (!jadwal.length) { toast.error("Belum ada jadwal untuk di-export"); return; }
    const safeName = petugas.nama.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    const stamp = new Date().toISOString().slice(0, 10);
    const headers = ["Hari Ke", "Tanggal", "Jam", "Area / Blok", "Tugas", "Status", "Lembur", "Upah"];
    const upahHarian = rekap?.upah_harian || 60000;
    const rows = jadwal.map((j) => [
      `H-${j.hari_ke}`, formatFullDate(j.tanggal), j.jam, j.area, j.status === "libur" ? "-" : j.tugas,
      statusLabel(j.status), j.is_lembur ? "Ya" : "-", j.status === "selesai" ? upahHarian : "-",
    ]);
    const summary = rekap ? [
      ["Nama Petugas", petugas.nama],
      ["Upah Harian", formatIDR(upahHarian)],
      ["Hari Normal Selesai", `${rekap.hari_selesai_normal} dari ${rekap.hari_normal}`],
      ["Hari Lembur Selesai", `${rekap.hari_selesai_lembur} dari ${rekap.hari_lembur}`],
      ["Hari Libur (tidak dibayar)", String(rekap.hari_libur || 0)],
      ["Upah Normal", formatIDR(rekap.upah_normal)],
      ["Upah Lembur", formatIDR(rekap.upah_lembur)],
      ["TOTAL UPAH", formatIDR(rekap.upah_total)],
    ] : [];
    const title = `Jadwal Kerja & Rekap Upah Tukang Taman — ${petugas.nama}`;
    const subtitle = "Bahan laporan & pembayaran gaji • Warga DM Residence Garut";
    if (kind === "xlsx") exportToExcel({ filename: `jadwal-tukang-taman-${safeName}-${stamp}.xlsx`, sheetName: "Jadwal Tukang Taman", headers, rows, summaryRows: summary });
    else if (kind === "pdf") exportToPDF({ filename: `jadwal-tukang-taman-${safeName}-${stamp}.pdf`, title, subtitle, headers, rows, summary });
    else exportToWord({ filename: `jadwal-tukang-taman-${safeName}-${stamp}.doc`, title, subtitle, headers, rows, summary });
    toast.success(`File ${kind.toUpperCase()} berhasil diunduh`);
  };

  const todayISO = new Date().toISOString().slice(0, 10);
  const jadwalHariIni = useMemo(() => jadwal.find((j) => j.tanggal === todayISO), [jadwal, todayISO]);

  const statusBadge = (j) => (
    <button
      onClick={() => canEdit && toggleStatus(j)}
      disabled={!canEdit}
      className={`text-xs font-bold px-2 py-1 rounded-full transition ${
        j.status === "selesai" ? "bg-emerald-500 text-white hover:bg-emerald-600" :
        j.status === "berjalan" ? "bg-amber-500 text-white hover:bg-amber-600" :
        j.status === "libur" ? "bg-slate-500 text-white hover:bg-slate-600" :
        "bg-slate-200 text-slate-600 hover:bg-slate-300"
      }`}
      data-testid={`jadwal-taman-status-${j.hari_ke}`}
    >
      {j.status === "selesai" ? "✓ SELESAI" : j.status === "berjalan" ? "⏳ BERJALAN" : j.status === "libur" ? "LIBUR" : "BELUM"}
    </button>
  );

  return (
    <Card className="border-emerald-200">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-lg bg-emerald-100 grid place-items-center"><CalendarDays className="h-5 w-5 text-emerald-700" /></div>
              <div>
                <h3 className="font-bold text-slate-800" style={{ fontFamily: "Poppins, sans-serif" }}>Jadwal Kerja Tukang Taman — {petugas.nama}</h3>
                <p className="text-xs text-slate-500">Rotasi pembersihan blok — fleksibel: tandai libur, tambah hari kerja, atau lanjutkan ke bulan berikutnya</p>
              </div>
            </div>
          </div>
          {canEdit && (
            <div className="flex gap-2 flex-wrap">
              {jadwal.length > 0 && <Button variant="outline" size="sm" onClick={removeAll} className="text-red-600 border-red-300 hover:bg-red-50" data-testid="jadwal-taman-clear-btn"><Trash2 className="h-3.5 w-3.5 mr-1" />Hapus Semua</Button>}
              {jadwal.length > 0 && <Button variant="outline" size="sm" onClick={lanjutkan} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid="jadwal-taman-lanjutkan-btn"><CalendarPlus className="h-3.5 w-3.5 mr-1" />Lanjutkan Bulan Berikutnya</Button>}
              <Button size="sm" className="brand-btn" onClick={() => setGenOpen(true)} data-testid="jadwal-taman-generate-btn"><Wand2 className="h-3.5 w-3.5 mr-1" />{jadwal.length > 0 ? "Regenerate Jadwal" : "Generate Jadwal Kerja"}</Button>
            </div>
          )}
        </div>

        {/* Export Laporan & Rekap Gaji */}
        {jadwal.length > 0 && (
          <div className="rounded-xl border border-slate-200 bg-white p-3 flex items-center gap-2 flex-wrap" data-testid="jadwal-taman-export-bar">
            <FileDown className="h-4 w-4 text-emerald-700" />
            <span className="text-xs font-bold text-slate-700 mr-1">Unduh Jadwal & Rekap Gaji:</span>
            <Button variant="outline" size="sm" onClick={() => doExport("pdf")} data-testid="jadwal-taman-export-pdf"><FileText className="h-3.5 w-3.5 mr-1 text-rose-600" />PDF</Button>
            <Button variant="outline" size="sm" onClick={() => doExport("docx")} data-testid="jadwal-taman-export-docx"><FileDown className="h-3.5 w-3.5 mr-1 text-blue-600" />Word</Button>
            <Button variant="outline" size="sm" onClick={() => doExport("xlsx")} data-testid="jadwal-taman-export-xlsx"><FileSpreadsheet className="h-3.5 w-3.5 mr-1 text-emerald-600" />Excel</Button>
          </div>
        )}

        {/* Upah Harian Editable */}
        <div className="rounded-xl border-2 border-amber-200 bg-amber-50/60 p-3 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <CircleDollarSign className="h-5 w-5 text-amber-700" />
            <Label className="font-bold text-amber-900 whitespace-nowrap">Upah Harian:</Label>
          </div>
          <Input type="number" value={upahLocal} onChange={(e) => setUpahLocal(e.target.value)} className="w-40 tabular-nums font-mono" disabled={!canEdit} data-testid="upah-harian-input" />
          <span className="text-sm text-slate-600">= {formatIDR(parseInt(upahLocal) || 0)} / hari</span>
          {canEdit && (parseInt(upahLocal) || 0) !== (theme?.upahHarianTukangTaman || 60000) && (
            <Button size="sm" onClick={saveUpah} disabled={savingUpah} className="brand-btn" data-testid="upah-save-btn">
              {savingUpah ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Menyimpan...</> : <><Check className="h-3 w-3 mr-1" />Simpan</>}
            </Button>
          )}
        </div>

        {/* Rekap */}
        {rekap && rekap.total_hari > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="jadwal-taman-rekap">
            <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50/60 p-3">
              <div className="text-[10px] uppercase font-bold text-emerald-700 tracking-wider">Hari Normal</div>
              <div className="text-2xl font-black text-emerald-800 tabular-nums" style={{ fontFamily: "Poppins, sans-serif" }}>{rekap.hari_normal}</div>
              <div className="text-[11px] text-emerald-600">selesai: {rekap.hari_selesai_normal}</div>
            </div>
            <div className="rounded-xl border-2 border-rose-200 bg-rose-50/60 p-3">
              <div className="text-[10px] uppercase font-bold text-rose-700 tracking-wider">Hari Lembur</div>
              <div className="text-2xl font-black text-rose-800 tabular-nums" style={{ fontFamily: "Poppins, sans-serif" }}>{rekap.hari_lembur}</div>
              <div className="text-[11px] text-rose-600">selesai: {rekap.hari_selesai_lembur}</div>
            </div>
            <div className="rounded-xl border-2 border-slate-300 bg-slate-50 p-3">
              <div className="text-[10px] uppercase font-bold text-slate-600 tracking-wider">Hari Libur</div>
              <div className="text-2xl font-black text-slate-700 tabular-nums" style={{ fontFamily: "Poppins, sans-serif" }}>{rekap.hari_libur || 0}</div>
              <div className="text-[11px] text-slate-500">tidak dibayar</div>
            </div>
            <div className="rounded-xl border-2 border-slate-200 bg-white p-3">
              <div className="text-[10px] uppercase font-bold text-slate-600 tracking-wider">Hari Selesai</div>
              <div className="text-2xl font-black text-slate-800 tabular-nums" style={{ fontFamily: "Poppins, sans-serif" }}>{rekap.hari_selesai}<span className="text-sm text-slate-400">/{rekap.total_hari}</span></div>
              <div className="text-[11px] text-slate-500">progress {Math.round(rekap.hari_selesai / rekap.total_hari * 100)}%</div>
            </div>
            <div className="rounded-xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-emerald-50 p-3">
              <div className="text-[10px] uppercase font-bold text-amber-700 tracking-wider">Total Upah</div>
              <div className="text-xl md:text-2xl font-black brand-text tabular-nums" style={{ fontFamily: "Poppins, sans-serif" }}>{formatIDR(rekap.upah_total)}</div>
              <div className="text-[11px] text-slate-500">normal {formatIDR(rekap.upah_normal)} + lembur {formatIDR(rekap.upah_lembur)}</div>
            </div>
          </div>
        )}

        {/* Highlight Hari Ini */}
        {jadwalHariIni && (
          <div className={`rounded-xl border-2 p-3 flex items-center gap-3 shadow-sm ${jadwalHariIni.status === "libur" ? "border-slate-300 bg-slate-50" : "border-emerald-400 bg-gradient-to-r from-emerald-50 to-emerald-100"}`}>
            <div className={`h-11 w-11 rounded-full text-white grid place-items-center font-black text-lg shrink-0 ${jadwalHariIni.status === "libur" ? "bg-slate-400" : "bg-emerald-500"}`} style={{ fontFamily: "Poppins, sans-serif" }}>{jadwalHariIni.hari_ke}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className="bg-emerald-600 text-white hover:bg-emerald-600">HARI INI</Badge>
                {jadwalHariIni.status === "libur" && <Badge className="bg-slate-500 text-white hover:bg-slate-500">LIBUR / TIDAK MASUK</Badge>}
                {jadwalHariIni.is_lembur && <Badge className="bg-rose-500 text-white hover:bg-rose-500">LEMBUR</Badge>}
                <span className="text-sm font-bold text-emerald-900">{jadwalHariIni.area}</span>
              </div>
              <div className="text-xs text-emerald-800 mt-0.5">{formatFullDate(jadwalHariIni.tanggal)} • {jadwalHariIni.jam} WIB</div>
              <div className="text-xs text-slate-600 mt-0.5 line-clamp-1">{jadwalHariIni.tugas}</div>
            </div>
          </div>
        )}

        {/* Table Jadwal */}
        {loading ? (
          <div className="text-center py-8 text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline mr-1" />Memuat...</div>
        ) : jadwal.length === 0 ? (
          <div className="text-center py-10 border-2 border-dashed border-slate-200 rounded-xl">
            <Timer className="h-8 w-8 mx-auto text-slate-400 mb-2" />
            <div className="text-sm text-slate-500">Belum ada jadwal. Klik <b>Generate Jadwal Kerja</b> untuk membuat rotasi otomatis (15 hari normal + lembur bila diperlukan).</div>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border">
            <table className="w-full text-sm">
              <thead className="bg-emerald-700 text-white">
                <tr>
                  <th className="p-2 text-center w-12">H-#</th>
                  <th className="p-2 text-left">Tanggal & Jam</th>
                  <th className="p-2 text-left">Area / Blok</th>
                  <th className="p-2 text-left hidden md:table-cell">Tugas</th>
                  <th className="p-2 text-center">Status</th>
                  <th className="p-2 text-right">Upah</th>
                  {canEdit && <th className="p-2 text-right">Aksi</th>}
                </tr>
              </thead>
              <tbody data-testid="jadwal-taman-table">
                {jadwal.map((j) => (
                  <tr key={j.id} className={`border-t border-emerald-100 ${j.tanggal === todayISO ? "bg-emerald-50/70" : ""} ${j.is_lembur && j.status !== "libur" ? "bg-rose-50/40" : ""} ${j.status === "libur" ? "bg-slate-50/70" : ""}`}>
                    <td className="p-2 text-center font-black text-emerald-800 tabular-nums" style={{ fontFamily: "Poppins, sans-serif" }}>{j.hari_ke}</td>
                    <td className="p-2 whitespace-nowrap">
                      <div className="text-slate-800 font-semibold">{formatFullDate(j.tanggal)}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{j.jam}</div>
                    </td>
                    <td className="p-2">
                      <div className="font-bold text-slate-800">{j.area}</div>
                      <div className="text-[11px] text-slate-500">Est. {j.estimasi_hari} hari</div>
                      {j.is_lembur && j.status !== "libur" && <Badge className="bg-rose-500 text-white hover:bg-rose-500 mt-1 text-[9px]">LEMBUR</Badge>}
                    </td>
                    <td className="p-2 text-slate-600 hidden md:table-cell max-w-xs">
                      <div className="line-clamp-2">{j.status === "libur" ? <span className="italic text-slate-400">Libur / tidak masuk — pekerjaan bergeser</span> : j.tugas}</div>
                      {j.catatan && <div className="text-[11px] text-amber-700 italic mt-0.5">📝 {j.catatan}</div>}
                    </td>
                    <td className="p-2 text-center">{statusBadge(j)}</td>
                    <td className="p-2 text-right whitespace-nowrap">
                      <div className={`font-black tabular-nums ${j.status === "selesai" ? (j.is_lembur ? "text-rose-700" : "text-emerald-700") : "text-slate-400"}`} style={{ fontFamily: "Poppins, sans-serif" }}>
                        {j.status === "selesai" ? formatIDR(rekap?.upah_harian || 60000) : "—"}
                      </div>
                    </td>
                    {canEdit && (
                      <td className="p-2 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" title="Buat Laporan Kegiatan hari ini" onClick={() => setLapPrefill({ tanggal: j.tanggal, kategori: "tukang_taman", petugas_nama: petugas.nama, jam: j.jam })} className="h-7 w-7 p-0 text-emerald-700" data-testid={`jadwal-taman-laporan-${j.hari_ke}`}><ClipboardList className="h-3.5 w-3.5" /></Button>
                        {j.status !== "libur" && (
                          <Button size="sm" variant="ghost" title="Tambah 1 hari kerja untuk area ini (jadwal bergeser)" onClick={() => tambahHari(j)} className="h-7 w-7 p-0 text-emerald-700" data-testid={`jadwal-taman-add-day-${j.hari_ke}`}><Plus className="h-3.5 w-3.5" /></Button>
                        )}
                        {j.status !== "selesai" && j.status !== "libur" && (
                          <Button size="sm" variant="ghost" title="Tandai LIBUR / tidak masuk (jadwal bergeser)" onClick={() => markLibur(j)} className="h-7 w-7 p-0 text-slate-500" data-testid={`jadwal-taman-libur-${j.hari_ke}`}><CalendarOff className="h-3.5 w-3.5" /></Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => openEdit(j)} className="h-7 w-7 p-0" data-testid={`jadwal-taman-edit-${j.hari_ke}`}><Edit3 className="h-3.5 w-3.5" /></Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Generate Dialog */}
        <Dialog open={genOpen} onOpenChange={setGenOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Wand2 className="h-5 w-5 text-emerald-700" />Generate Jadwal Kerja Tukang Taman</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Tanggal Mulai (Hari ke-1) *</Label>
                <Input type="date" value={genForm.tanggal_mulai} onChange={(e) => setGenForm({ ...genForm, tanggal_mulai: e.target.value })} data-testid="jadwal-taman-gen-tanggal" />
              </div>
              <div>
                <Label>Jam Default (bisa di-edit per hari)</Label>
                <Input type="time" value={genForm.jam_default} onChange={(e) => setGenForm({ ...genForm, jam_default: e.target.value })} />
              </div>
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900 space-y-1">
                <div className="font-bold">Rotasi Default:</div>
                <div>• Hari 1–4: Blok A, B, E (4 hari)</div>
                <div>• Hari 5–8: Blok C, D, F (4 hari)</div>
                <div>• Hari 9–14: Blok G, H, I, J (6 hari)</div>
                <div>• Hari 15–17: Blok K, L, Taman Depan (2–3 hari) — hari ke-16+ ditandai LEMBUR</div>
                <div className="pt-1 border-t border-emerald-200 mt-1 font-semibold">Semua entri dapat di-edit inline setelah generate.</div>
              </div>
              {jadwal.length > 0 && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">⚠️ {jadwal.length} baris jadwal existing akan DIHAPUS dan diganti dengan yang baru. Gunakan <b>Lanjutkan Bulan Berikutnya</b> jika ingin melanjutkan tanpa menghapus.</div>}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setGenOpen(false)}>Batal</Button>
              <Button className="brand-btn" onClick={generate} data-testid="jadwal-taman-gen-submit"><Wand2 className="h-4 w-4 mr-1" />Generate</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Edit Jadwal Hari ke-{editForm?.hari_ke}</DialogTitle></DialogHeader>
            {editForm && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Tanggal</Label><Input type="date" value={editForm.tanggal} onChange={(e) => setEditForm({ ...editForm, tanggal: e.target.value })} /></div>
                  <div><Label>Jam</Label><Input type="time" value={editForm.jam} onChange={(e) => setEditForm({ ...editForm, jam: e.target.value })} /></div>
                </div>
                <div><Label>Area / Blok</Label><Input value={editForm.area} onChange={(e) => setEditForm({ ...editForm, area: e.target.value })} placeholder="Blok A, B, E" /></div>
                <div><Label>Tugas Kerja</Label><Textarea rows={3} value={editForm.tugas} onChange={(e) => setEditForm({ ...editForm, tugas: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Estimasi Hari</Label><Input type="number" min={1} value={editForm.estimasi_hari} onChange={(e) => setEditForm({ ...editForm, estimasi_hari: e.target.value })} /></div>
                  <div><Label>Status</Label>
                    <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                      <SelectTrigger data-testid="jadwal-taman-edit-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="belum">Belum</SelectItem>
                        <SelectItem value="berjalan">Berjalan</SelectItem>
                        <SelectItem value="selesai">Selesai</SelectItem>
                        <SelectItem value="libur">Libur / Tidak Masuk</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editForm.is_lembur} onChange={(e) => setEditForm({ ...editForm, is_lembur: e.target.checked })} />Tandai sebagai LEMBUR</label>
                <div><Label>Catatan</Label><Textarea rows={2} value={editForm.catatan || ""} onChange={(e) => setEditForm({ ...editForm, catatan: e.target.value })} placeholder="Catatan tambahan..." /></div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOpen(false)}>Batal</Button>
              <Button className="brand-btn" onClick={saveEdit} data-testid="jadwal-taman-edit-save">Simpan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <LaporanKegiatanDialog open={!!lapPrefill} onOpenChange={() => setLapPrefill(null)} prefill={lapPrefill} />
      </CardContent>
    </Card>
  );
};

export default JadwalTaman;
